import { supabase } from '../lib/supabase'

export interface NotificationMeta {
  id: string
  title: string
  body: string
  created_by: string
  created_at: string
}

export interface NotificationRecipientItem {
  id: string // notification_recipients.id
  notification_id: string
  user_id: string
  read_at: string | null
  created_at: string
  notification?: NotificationMeta
}

export const notificationsService = {
  async sendToGroups(params: {
    title: string
    body: string
    userGroupIds?: string[]
    userIds?: string[]
    /** Remetente exibido na notificação (padrão: usuário autenticado). */
    createdByUserId?: string
  }) {
    const { title, body, userGroupIds, userIds, createdByUserId } = params

    const hasGroupRecipients = !!(userGroupIds && userGroupIds.length > 0)
    const hasUserRecipients = Array.isArray(userIds) && userIds.length > 0

    if (!hasGroupRecipients && !hasUserRecipients) {
      return { data: null as any, error: new Error('Nenhum destinatário informado') }
    }

    const { data, error } = await supabase.rpc('create_notification', {
      p_title: title,
      p_body: body,
      p_user_group_ids: hasGroupRecipients ? userGroupIds : null,
      p_user_ids: hasUserRecipients ? userIds : null,
      p_created_by: createdByUserId ?? null,
    })

    return { data, error }
  },

  async fetchUnreadCount(userId?: string) {
    let query = supabase
      .from('notification_recipients')
      .select('*', { count: 'exact', head: true })
      .is('read_at', null)
      .is('deleted_at', null)

    if (userId) {
      query = query.eq('user_id', userId)
    }

    const { count, error } = await query
    return { count: count || 0, error }
  },

  async fetchLatest(limit: number = 15, userId?: string) {
    let query = supabase
      .from('notification_recipients')
      .select('id, notification_id, user_id, read_at, created_at, notification:notification_id(id, title, body, created_by, created_at)')
      .is('deleted_at', null)

    if (userId) {
      query = query.eq('user_id', userId)
    }

    query = query.order('created_at', { ascending: false }).limit(limit)

    const { data, error } = await query

    const items = (data || []).map((row: any) => ({
      ...row,
      notification: Array.isArray(row.notification) ? row.notification[0] : row.notification
    })) as NotificationRecipientItem[]

    return { data: items, error }
  },

  async markAsRead(recipientId: string) {
    const { data, error } = await supabase
      .from('notification_recipients')
      .update({ read_at: new Date().toISOString() })
      .eq('id', recipientId)
      .select()
      .single()

    return { data, error }
  },

  async deleteRecipient(recipientId: string) {
    const { error } = await supabase
      .from('notification_recipients')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', recipientId)

    return { error }
  },

  async clearAllForUser(userId: string) {
    const { error } = await supabase
      .from('notification_recipients')
      .update({ deleted_at: new Date().toISOString() })
      .eq('user_id', userId)
      .is('deleted_at', null)

    return { error }
  },

  subscribeRealtime(userId: string, onChange: () => void) {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null
    let pollInterval: ReturnType<typeof setInterval> | null = null
    let subscribeTimeout: ReturnType<typeof setTimeout> | null = null
    let subscribed = false

    const debouncedOnChange = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(onChange, 300)
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        onChange()
      }
    }

    const startPollingFallback = () => {
      if (pollInterval) return
      if (import.meta.env.DEV) {
        console.warn('[notifications] Realtime não conectou; usando polling fallback (60s)')
      }
      pollInterval = setInterval(onChange, 60_000)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    const channel = supabase
      .channel(`notifications:${userId}`)
      .on('postgres_changes' as any, {
        event: 'INSERT',
        schema: 'public',
        table: 'notification_recipients',
        filter: `user_id=eq.${userId}`
      }, debouncedOnChange)
      .on('postgres_changes' as any, {
        event: 'UPDATE',
        schema: 'public',
        table: 'notification_recipients',
        filter: `user_id=eq.${userId}`
      }, debouncedOnChange)
      .on('postgres_changes' as any, {
        event: 'DELETE',
        schema: 'public',
        table: 'notification_recipients',
        filter: `user_id=eq.${userId}`
      }, debouncedOnChange)
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          subscribed = true
          if (subscribeTimeout) {
            clearTimeout(subscribeTimeout)
            subscribeTimeout = null
          }
          if (pollInterval) {
            clearInterval(pollInterval)
            pollInterval = null
          }
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          if (import.meta.env.DEV) {
            console.warn('[notifications] Realtime channel error:', status)
          }
          startPollingFallback()
        }
      })

    subscribeTimeout = setTimeout(() => {
      if (!subscribed) {
        startPollingFallback()
      }
    }, 5000)

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      if (subscribeTimeout) clearTimeout(subscribeTimeout)
      if (pollInterval) clearInterval(pollInterval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      try { supabase.removeChannel(channel) } catch {}
    }
  }
}

/** Dispara notificação in-app no padrão do sistema (RPC `create_notification`). */
export async function dispatchAppNotification(params: {
  title: string
  body: string
  userGroupIds?: string[]
  userIds?: string[]
  createdByUserId?: string
}) {
  return notificationsService.sendToGroups(params)
}

